import React, { useCallback } from 'react';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Heart } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { clsx } from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { Folder } from '../../models';
import { useFavoriteTemplatingFolders } from '../../hooks/useFavoriteTemplatingFolders';

const messages = defineMessages({
  favoriteFolder: {
    id: 'FavoriteFolderButton.favoriteFolder',
    defaultMessage: 'Favorite folder',
  },
  unfavoriteFolder: {
    id: 'FavoriteFolderButton.unfavoriteFolder',
    defaultMessage: 'Remove favorite folder',
  },
});

const useStyles = makeStyles(theme => ({
  favoriteFolderButton: {
    padding: 0,
  },
  favorited: {
    '& svg': {
      stroke: theme.palette.red.main,
    },
    '& svg path': {
      fill: theme.palette.red.main,
    },
  },
}));

type FavoriteFolderButtonProps = {
  folder: Folder;
  className?: string;
};

export const FavoriteFolderButton: React.FC<FavoriteFolderButtonProps> = ({
  folder,
  className,
}) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const { isFavoriteFolder, toggleFavoriteFolder } =
    useFavoriteTemplatingFolders();
  const isFavorite = isFavoriteFolder(folder);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      toggleFavoriteFolder(folder);
    },
    [folder, toggleFavoriteFolder],
  );

  return (
    <IconButton
      size={0}
      icon={Heart}
      aria-label={formatMessage(
        isFavorite ? messages.unfavoriteFolder : messages.favoriteFolder,
      )}
      onClick={handleClick}
      className={clsx(
        className,
        styles.favoriteFolderButton,
        isFavorite && styles.favorited,
      )}
      color="inverse"
    />
  );
};
